<template>
  <AppDialog v-model="dialogOpen" :title="title" :max-width="maxWidth" :busy="loading" persistent :testid="testid">
    <!-- Rich consequence text goes in the default slot; `message` is a
         convenience fallback for simple plain-text confirmations. -->
    <slot>{{ message }}</slot>

    <template #actions>
      <v-spacer />
      <v-btn variant="text" :disabled="loading" :data-testid="`${testid}-cancel`" @click="onCancel">
        {{ cancelLabel }}
      </v-btn>
      <v-btn
        :color="confirmColor"
        variant="flat"
        :loading="loading"
        :disabled="confirmDisabled"
        :prepend-icon="confirmIcon"
        :aria-label="confirmLabel"
        :data-testid="`${testid}-confirm`"
        @click="onConfirm"
      >
        {{ confirmLabel }}
      </v-btn>
    </template>
  </AppDialog>
</template>

<script setup lang="ts">
import { computed } from "vue";

import AppDialog from "@/components/common/AppDialog.vue";

/**
 * Reusable confirmation dialog for destructive / irreversible actions.
 *
 * Composes {@link AppDialog} for the modal shell, title association, and stable
 * test ids while owning confirmation semantics. The parent owns the action:
 * clicking confirm emits `confirm` and the parent is responsible for running
 * the mutation, driving `loading`, and closing the dialog (set `model-value` to
 * false) once it settles. This keeps error handling and post-action refresh
 * where the data lives.
 *
 * Test ids derive from `testid`: the dialog is `${testid}-dialog`, the confirm
 * button `${testid}-confirm`, and the cancel button `${testid}-cancel`.
 */
const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    title: string;
    /** Plain-text body; ignored when the default slot is used. */
    message?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmColor?: string;
    /** Optional @mdi/js path string rendered before the confirm label. */
    confirmIcon?: string;
    /** Parent-owned in-flight state for the confirm action. */
    loading?: boolean;
    /** Blocks confirm while the dialog's own input is incomplete — a required reason,
     * a typed-name check. Use it only for that: a state the mutation would refuse
     * belongs on whether the trigger renders at all. */
    confirmDisabled?: boolean;
    maxWidth?: string | number;
    testid?: string;
  }>(),
  {
    message: "",
    confirmLabel: "Confirm",
    cancelLabel: "Cancel",
    confirmColor: "error",
    confirmIcon: undefined,
    loading: false,
    confirmDisabled: false,
    maxWidth: 440,
    testid: "confirm",
  },
);

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  confirm: [];
  cancel: [];
}>();

const dialogOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
});

function onCancel() {
  dialogOpen.value = false;
  emit("cancel");
}

function onConfirm() {
  emit("confirm");
}
</script>
